import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useCatalog } from "@/lib/catalog";
import { apiPost } from "@/lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";

export function Field({ label, required, children, testid }) {
  const { t } = useLang();
  return (
    <label className="block" data-testid={testid}>
      <span className="mb-1.5 block text-sm font-medium text-marine">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}

export function SuccessScreen({ message, onReset }) {
  const { t } = useLang();
  return (
    <div className="mg-card mx-auto max-w-xl p-10 text-center" data-testid="form-success">
      <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
      <h2 className="mt-4 font-display text-2xl font-bold text-marine">{t("form.successTitle")}</h2>
      <p className="mt-3 text-sm leading-relaxed text-slate-500">{message}</p>
      <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
        <Link to="/" className="btn-primary" data-testid="success-home-button">
          {t("form.backHome")}
        </Link>
        {onReset && (
          <button onClick={onReset} className="btn-outline" data-testid="success-new-request-button">
            {t("form.newRequest")}
          </button>
        )}
      </div>
    </div>
  );
}

export function LeadForm({ endpoint, withMessageRequired = false, withDemoFields = false, testid }) {
  const { t, lang } = useLang();
  const { catalog } = useCatalog();
  const empty = {
    name: "",
    email: "",
    company: "",
    phone: "",
    country: "",
    sector: "",
    company_size: "",
    preferred_date: "",
    subject: "",
    message: "",
  };
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle"); // idle | sending | success
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
    try {
      await apiPost(endpoint, payload);
      setStatus("success");
    } catch (err) {
      const detail = err.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join(" · ")
            : t("common.error")
      );
      setStatus("idle");
    }
  };

  if (status === "success") {
    return (
      <SuccessScreen
        message={withDemoFields ? t("form.successDemo") : t("form.successContact")}
        onReset={() => {
          setForm(empty);
          setStatus("idle");
        }}
      />
    );
  }

  const sizes = t("form.sizes");
  const sectors = catalog?.sectors || [];

  return (
    <form onSubmit={submit} className="mg-card mx-auto max-w-3xl p-6 sm:p-8" data-testid={testid}>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={t("form.name")} required testid="field-name">
          <input className="input-field" value={form.name} onChange={set("name")} required data-testid="input-name" />
        </Field>
        <Field label={t("form.email")} required testid="field-email">
          <input type="email" className="input-field" value={form.email} onChange={set("email")} required data-testid="input-email" />
        </Field>
        <Field label={t("form.company")} testid="field-company">
          <input className="input-field" value={form.company} onChange={set("company")} data-testid="input-company" />
        </Field>
        <Field label={t("form.phone")} testid="field-phone">
          <input className="input-field" value={form.phone} onChange={set("phone")} data-testid="input-phone" />
        </Field>
        <Field label={t("form.country")} testid="field-country">
          <input className="input-field" value={form.country} onChange={set("country")} data-testid="input-country" />
        </Field>
        <Field label={t("form.sector")} testid="field-sector">
          <select className="input-field" value={form.sector} onChange={set("sector")} data-testid="input-sector">
            <option value="">{t("form.selectPlaceholder")}</option>
            {sectors.map((s) => (
              <option key={s.key} value={s.key}>
                {lang === "fr" ? s.name_fr : s.name_en}
              </option>
            ))}
            <option value="other">{t("form.sectorOther")}</option>
          </select>
        </Field>
        {withDemoFields && (
          <>
            <Field label={t("form.companySize")} testid="field-company-size">
              <select className="input-field" value={form.company_size} onChange={set("company_size")} data-testid="input-company-size">
                <option value="">{t("form.selectPlaceholder")}</option>
                {sizes.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("form.preferredDate")} testid="field-preferred-date">
              <input type="date" className="input-field" value={form.preferred_date} onChange={set("preferred_date")} data-testid="input-preferred-date" />
            </Field>
          </>
        )}
        <Field label={t("form.subject")} testid="field-subject">
          <input className="input-field" value={form.subject} onChange={set("subject")} data-testid="input-subject" />
        </Field>
        <div className="sm:col-span-2">
          <Field label={t("form.message")} required={withMessageRequired} testid="field-message">
            <textarea
              rows={5}
              className="input-field resize-y"
              value={form.message}
              onChange={set("message")}
              required={withMessageRequired}
              data-testid="input-message"
            />
          </Field>
        </div>
      </div>

      {error && (
        <div className="mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="form-error">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button type="submit" disabled={status === "sending"} className="btn-primary mt-6 w-full sm:w-auto" data-testid="form-submit-button">
        {status === "sending" ? t("form.sending") : withDemoFields ? t("form.submit") : t("form.submitContact")}
      </button>
    </form>
  );
}
